import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {activeCommentAtom} from 'atoms/comment';
import {useAtomValue} from 'jotai';
import {
  $createRangeSelection,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  $setSelection,
  ElementNode,
  LexicalNode,
  SKIP_COLLAB_TAG,
} from 'lexical';
import {useEffect, useRef} from 'react';

interface SavedSelection {
  text: string;
  anchorOffset: number;
  focusOffset: number;
  anchorKey: string;
  focusKey: string;
  // Additional context for better text search fallback
  beforeText: string; // Text before the selection
  afterText: string; // Text after the selection
  // Block-level nodes containing the selection (for scoped search)
  anchorBlockKey: string | null;
  focusBlockKey: string | null;
}

/**
 * Preserves text selections when collaborative edits change the DOM structure.
 * Saves selections before collab updates and restores them if they become invalid.
 */
export const SelectionPreservationPlugin = (): null => {
  const [editor] = useLexicalComposerContext();
  const savedSelectionRef = useRef<SavedSelection | null>(null);
  const editorHasFocusRef = useRef(false);
  const activeComment = useAtomValue(activeCommentAtom);

  useEffect(() => {
    const commentBoxOpen = document.querySelectorAll('[data-comment-box]').length > 0;
    if (commentBoxOpen || activeComment !== null) {
      savedSelectionRef.current = null;
    }
  }, [activeComment]);

  useEffect(() => {
    const editorElement = editor.getRootElement();
    if (!editorElement) return;

    // Track when editor has focus - if user is focused, they're actively editing
    const handleFocus = () => {
      editorHasFocusRef.current = true;
    };

    const handleBlur = () => {
      editorHasFocusRef.current = false;
    };

    // Track mouse/keyboard interactions to clear saved selection
    const handleInteraction = () => {
      savedSelectionRef.current = null;
    };

    editorElement.addEventListener('focus', handleFocus, true);
    editorElement.addEventListener('blur', handleBlur, true);
    editorElement.addEventListener('mousedown', handleInteraction);
    editorElement.addEventListener('keydown', handleInteraction);

    // Set initial focus state
    editorHasFocusRef.current = editorElement.contains(document.activeElement);

    return () => {
      editorElement.removeEventListener('focus', handleFocus, true);
      editorElement.removeEventListener('blur', handleBlur, true);
      editorElement.removeEventListener('mousedown', handleInteraction);
      editorElement.removeEventListener('keydown', handleInteraction);
    };
  }, [editor]);

  useEffect(() => {
    const unregisterUpdateListener = editor.registerUpdateListener(
      ({editorState, prevEditorState, tags}) => {
        // Check if this is a collaborative update (no SKIP_COLLAB_TAG)
        const isCollabUpdate = !tags.has(SKIP_COLLAB_TAG) && !tags.has('history-merge');

        if (isCollabUpdate) {
          // Check if comment box is open BEFORE the collab update
          const commentBoxWasOpen =
            document.querySelectorAll('[data-comment-box]').length > 0 || activeComment !== null;

          // Before collaborative update: save current selection if editor doesn't have focus
          if (!editorHasFocusRef.current && !commentBoxWasOpen) {
            prevEditorState.read(() => {
              const selection = $getSelection();

              if ($isRangeSelection(selection) && !selection.isCollapsed()) {
                const text = selection.getTextContent();
                if (text.trim()) {
                  const anchor = selection.anchor;
                  const focus = selection.focus;

                  // Get the block-level nodes containing the selection
                  const anchorNode = anchor.getNode();
                  const focusNode = focus.getNode();
                  const anchorBlock = $findContainingBlock(anchorNode);
                  const focusBlock = $findContainingBlock(focusNode);

                  // Get surrounding context for better text search fallback
                  const root = $getRoot();
                  const fullText = root.getTextContent();
                  const selectionIndex = fullText.indexOf(text);

                  const contextLength = 50; // Characters before/after for context
                  const beforeText =
                    selectionIndex > 0
                      ? fullText.substring(
                          Math.max(0, selectionIndex - contextLength),
                          selectionIndex
                        )
                      : '';
                  const afterText =
                    selectionIndex !== -1
                      ? fullText.substring(
                          selectionIndex + text.length,
                          selectionIndex + text.length + contextLength
                        )
                      : '';

                  savedSelectionRef.current = {
                    text,
                    anchorOffset: anchor.offset,
                    focusOffset: focus.offset,
                    anchorKey: anchor.key,
                    focusKey: focus.key,
                    beforeText,
                    afterText,
                    anchorBlockKey: anchorBlock?.getKey() ?? null,
                    focusBlockKey: focusBlock?.getKey() ?? null,
                  };
                }
              }
            });
          }

          // After collaborative update: check if selection is still valid
          // Use requestAnimationFrame + setTimeout to ensure DOM has fully updated
          requestAnimationFrame(() => {
            setTimeout(() => {
              if (editorHasFocusRef.current) {
                savedSelectionRef.current = null;
                return;
              }

              // Don't restore if a comment box is open
              const commentEditors = document.querySelectorAll('[data-comment-box]');
              const hasCommentBox = commentEditors.length > 0;
              const hasActiveComment = activeComment !== null;
              const shouldSkip = hasCommentBox || hasActiveComment;

              if (shouldSkip) {
                savedSelectionRef.current = null;
                return;
              }

              editorState.read(() => {
                const currentSelection = $getSelection();

                if (savedSelectionRef.current) {
                  // Check if the selection became invalid
                  const isInvalid =
                    !$isRangeSelection(currentSelection) ||
                    currentSelection.isCollapsed() ||
                    !currentSelection.getTextContent().trim();

                  if (isInvalid) {
                    restoreSelection(editor, savedSelectionRef.current);
                  } else {
                    // Selection is valid, but check if it's the same selection
                    const currentText = currentSelection.getTextContent();
                    const savedText = savedSelectionRef.current.text;

                    // If the text content differs significantly, try to restore
                    if (currentText !== savedText) {
                      restoreSelection(editor, savedSelectionRef.current);
                    }
                  }

                  savedSelectionRef.current = null;
                }
              });
            }, 0);
          });
        } else {
          // For local updates, clear saved selection (user is making changes)
          savedSelectionRef.current = null;
        }
      }
    );

    return () => {
      unregisterUpdateListener();
    };
  }, [editor, activeComment]);

  return null;
};

/**
 * Find the containing block-level node (like ParagraphNode, HeadingNode, etc.)
 */
const $findContainingBlock = (node: LexicalNode): ElementNode | null => {
  let current: LexicalNode | null = node;

  while (current) {
    if (current instanceof ElementNode) {
      // Check if this is a block-level element
      const type = current.getType();
      if (
        type === 'paragraph' ||
        type === 'heading' ||
        type === 'list' ||
        type === 'listitem' ||
        type === 'quote' ||
        type === 'code'
      ) {
        return current;
      }
    }
    current = current.getParent();
  }

  return null;
};

/**
 * Search for text within specific block nodes and return the text nodes and offsets
 */
function searchWithinBlocks(
  blocks: ElementNode[],
  text: string,
  beforeText: string,
  afterText: string
): {startNode: LexicalNode; startOffset: number; endNode: LexicalNode; endOffset: number} | null {
  // Get the combined text from all blocks
  let fullText = '';
  const blockTexts: Array<{block: ElementNode; startOffset: number; text: string}> = [];

  for (const block of blocks) {
    const blockText = block.getTextContent();
    blockTexts.push({
      block,
      startOffset: fullText.length,
      text: blockText,
    });
    fullText += blockText;
  }

  // Try to find text using surrounding context for better accuracy
  let textIndex = -1;

  if (beforeText || afterText) {
    // Search for the pattern: beforeText + text + afterText
    const searchPattern = beforeText + text + afterText;
    const patternIndex = fullText.indexOf(searchPattern);

    if (patternIndex !== -1) {
      textIndex = patternIndex + beforeText.length;
    } else {
      // If full pattern doesn't match, try just beforeText + text
      if (beforeText) {
        const beforePattern = beforeText + text;
        const beforeIndex = fullText.indexOf(beforePattern);
        if (beforeIndex !== -1) {
          textIndex = beforeIndex + beforeText.length;
        }
      }

      // Try just text + afterText
      if (textIndex === -1 && afterText) {
        const afterPattern = text + afterText;
        const afterIndex = fullText.indexOf(afterPattern);
        if (afterIndex !== -1) {
          textIndex = afterIndex;
        }
      }
    }
  }

  // Fallback to simple search
  if (textIndex === -1) {
    textIndex = fullText.indexOf(text);
  }

  if (textIndex === -1) {
    return null;
  }

  // Collect all text nodes from the blocks
  const textNodes: Array<{node: LexicalNode; offset: number; endOffset: number}> = [];
  let currentOffset = 0;

  const collectTextNodes = (node: LexicalNode): void => {
    if ($isTextNode(node)) {
      const nodeLength = node.getTextContent().length;
      textNodes.push({
        node,
        offset: currentOffset,
        endOffset: currentOffset + nodeLength,
      });
      currentOffset += nodeLength;
    } else if (node instanceof ElementNode) {
      const children = node.getChildren();
      for (const child of children) {
        collectTextNodes(child);
      }
    }
  };

  for (const block of blocks) {
    collectTextNodes(block);
  }

  // Find which text nodes contain this range
  const startOffset = textIndex;
  const endOffset = textIndex + text.length;

  let startNode = null;
  let startNodeOffset = 0;
  let endNode = null;
  let endNodeOffset = 0;

  for (const {node, offset, endOffset: nodeEndOffset} of textNodes) {
    if (startNode === null && startOffset >= offset && startOffset < nodeEndOffset) {
      startNode = node;
      startNodeOffset = startOffset - offset;
    }

    if (endOffset > offset && endOffset <= nodeEndOffset) {
      endNode = node;
      endNodeOffset = endOffset - offset;
      break;
    }
  }

  if (startNode && endNode) {
    return {
      startNode,
      startOffset: startNodeOffset,
      endNode,
      endOffset: endNodeOffset,
    };
  }

  return null;
}

/**
 * Attempts to restore a selection that was lost due to DOM changes
 *
 * Uses a three-phase approach:
 * 1. Fast path: Try to use the saved node keys directly
 * 2. Medium path: Search within the saved block nodes only (scoped search)
 * 3. Slow path: Search the entire document with contextual text matching
 *
 * The scoped search dramatically improves accuracy by limiting search to
 * the paragraph/block where the selection was originally made.
 */
function restoreSelection(editor: any, savedSelection: SavedSelection): void {
  const {
    anchorKey,
    focusKey,
    anchorOffset,
    focusOffset,
    text,
    beforeText,
    afterText,
    anchorBlockKey,
    focusBlockKey,
  } = savedSelection;

  editor.update(() => {
    try {
      // PHASE 1: Try using the saved node keys (fast path)
      const anchorNode = editor.getElementByKey(anchorKey);
      const focusNode = editor.getElementByKey(focusKey);

      if (anchorNode && focusNode && $isTextNode(anchorNode) && $isTextNode(focusNode)) {
        const anchorTextLength = anchorNode.getTextContent().length;
        const focusTextLength = focusNode.getTextContent().length;

        if (anchorOffset <= anchorTextLength && focusOffset <= focusTextLength) {
          const restoredSelection = $createRangeSelection();
          restoredSelection.anchor.set(anchorKey, anchorOffset, 'text');
          restoredSelection.focus.set(focusKey, focusOffset, 'text');
          $setSelection(restoredSelection);
          return;
        }
      }
    } catch {
      // Keys might be invalid, fall through to block search
    }

    // PHASE 2: Search within the saved block nodes (medium path - much more accurate!)
    if (anchorBlockKey || focusBlockKey) {
      try {
        // Try to find the block nodes
        const anchorBlock = anchorBlockKey ? editor.getElementByKey(anchorBlockKey) : null;
        const focusBlock = focusBlockKey ? editor.getElementByKey(focusBlockKey) : null;

        const blocksToSearch: ElementNode[] = [];
        if (anchorBlock instanceof ElementNode) blocksToSearch.push(anchorBlock);
        if (focusBlock instanceof ElementNode && focusBlock !== anchorBlock) {
          blocksToSearch.push(focusBlock);
        }

        if (blocksToSearch.length > 0) {
          // Search within these specific blocks
          const result = searchWithinBlocks(blocksToSearch, text, beforeText, afterText);
          if (result) {
            const restoredSelection = $createRangeSelection();
            restoredSelection.anchor.set(result.startNode.getKey(), result.startOffset, 'text');
            restoredSelection.focus.set(result.endNode.getKey(), result.endOffset, 'text');
            $setSelection(restoredSelection);
            return;
          }
        }
      } catch {
        // Block search failed, fall through to full document search
      }
    }

    // PHASE 3: Search the entire document with context (slow path)
    try {
      const root = $getRoot();
      const result = searchWithinBlocks([root], text, beforeText, afterText);

      if (result) {
        const restoredSelection = $createRangeSelection();
        restoredSelection.anchor.set(result.startNode.getKey(), result.startOffset, 'text');
        restoredSelection.focus.set(result.endNode.getKey(), result.endOffset, 'text');
        $setSelection(restoredSelection);
      }
    } catch {
      // Restoration failed
    }
  });
}
